/* eslint-disable no-param-reassign */
import { useState, useEffect } from 'react';

import Box from '@zenbusiness/zui/Box';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import { produce } from 'immer';
import PropTypes from 'prop-types';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';

import { StepItemStyle } from './Common.styles';

// Step component
const Step = ({ name, isVisible, isActive }) => {
  const theme = useTheme();
  if (!isVisible) {
    return null;
  }

  return (
    <StepItemStyle>
      <Stack
        alignItems="baseline"
        direction="row"
      >
        <Box mr="4px">
          {isActive ? (
            <CheckCircleRoundedIcon
              fontSize="small"
              sx={{
                color: theme.palette.secondary.main
              }}
            />
          ) : (
            <CircleOutlinedIcon fontSize="small" />
          )}
        </Box>
        <Text size="sm">{name}</Text>
      </Stack>
    </StepItemStyle>
  );
};

Step.propTypes = {
  name: PropTypes.string.isRequired,
  isVisible: PropTypes.bool.isRequired,
  isActive: PropTypes.bool.isRequired
};

const updateSteps = (status, steps) =>
  produce(steps, (draft) => {
    const targetIndex = draft.findIndex((step) => step.status === status);

    if (targetIndex !== -1) {
      // Activate all steps up to and including the targetIndex
      for (let i = 0; i <= targetIndex; i += 1) {
        draft[i].isActive = true;
        draft[i].isVisible = true;
      }

      // Make the next step visible if it exists
      if (targetIndex + 1 < draft.length) {
        draft[targetIndex + 1].isVisible = true;
      }
    }
  });

// LoaderSteps component
const LoaderSteps = ({ steps: initialSteps, status }) => {
  const [steps, setSteps] = useState(initialSteps);

  useEffect(() => {
    setSteps((prevSteps) => updateSteps(status, prevSteps));
  }, [status]);

  return (
    <>
      {steps.map(({ name, isVisible, isActive }) => (
        <Step
          isActive={isActive}
          isVisible={isVisible}
          key={name}
          name={name}
        />
      ))}
    </>
  );
};

LoaderSteps.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      isVisible: PropTypes.bool,
      isActive: PropTypes.bool
    })
  ).isRequired,
  status: PropTypes.string.isRequired
};

export default LoaderSteps;
